import { useState, useEffect, useRef } from 'react';

// For WebSocket, if API_BASE_URL is empty (relative), use current window location
// In development (localhost), default to 8001 to avoid CRA proxy WebSocket issues
const getWsUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('http://', 'ws://').replace('https://', 'wss://');
  }
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'ws://localhost:8001';
  }
  return (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host;
};

const WS_URL = getWsUrl();

/**
 * Custom hook for real-time funding rate updates via WebSocket.
 * Falls back to REST API polling if WebSocket is unavailable.
 */
export function useFundingRateWebSocket(enabled = true) {
  const [rates, setRates] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const reconnectDelay = 5000; // 5 seconds

  useEffect(() => {
    // Don't connect if disabled
    if (!enabled) {
      return;
    }
    
    let mounted = true;

    const connect = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws/funding-rates`);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('WebSocket connected for funding rates');
          setIsConnected(true);
          setError(null);
          reconnectAttempts.current = 0;
          if (mounted) {
            // Clear any pending reconnection
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }
          }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'initial') {
              // Initial rates snapshot
              const ratesMap = {};
              Object.entries(data.rates || {}).forEach(([key, rateInfo]) => {
                ratesMap[key] = rateInfo;
              });
              if (mounted) {
                setRates(prev => ({ ...prev, ...ratesMap }));
              }
            } else if (data.type === 'update') {
              // Single rate update
              const key = `${data.exchange}_${data.pair}`;
              if (mounted) {
                setRates(prev => ({
                  ...prev,
                  [key]: data.funding_rate_info
                }));
              }
            } else if (data.type === 'ping') {
              // Respond to ping
              ws.send(JSON.stringify({ type: 'pong' }));
            }
          } catch (err) {
            console.error('Error parsing WebSocket message:', err);
          }
        };

        ws.onerror = (err) => {
          console.error('WebSocket error:', err);
          if (mounted) {
            setError('WebSocket connection error');
            setIsConnected(false);
          }
        };

        ws.onclose = () => {
          console.log('WebSocket disconnected');
          if (mounted) {
            setIsConnected(false);
            
            // Attempt to reconnect
            if (reconnectAttempts.current < maxReconnectAttempts) {
              reconnectAttempts.current += 1;
              console.log(`Attempting to reconnect (${reconnectAttempts.current}/${maxReconnectAttempts})...`);
              reconnectTimeoutRef.current = setTimeout(() => {
                if (mounted) {
                  connect();
                }
              }, reconnectDelay);
            } else {
              console.error('Max reconnection attempts reached. Falling back to REST polling.');
              setError('WebSocket unavailable, using REST polling');
            }
          }
        };
      } catch (err) {
        console.error('Error creating WebSocket:', err);
        if (mounted) {
          setError('Failed to create WebSocket connection');
          setIsConnected(false);
        }
      }
    };

    // Connect on mount
    connect();

    // Cleanup on unmount
    return () => {
      mounted = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [enabled]);

  return { rates, isConnected, error };
}

